/** DarkMultiSelect — searchable multi-select dropdown with checkboxes,
 * selection count and reset. Values stay canonical (slugs). */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";

export default function DarkMultiSelect({
  values = [],
  onChange,
  options = [],
  placeholder = "",
  searchPlaceholder = "",
  selectedLabel = (n) => `${n}`,
  resetLabel = "Reset",
  testid,
  loading = false,
  error = false,
  onRetry,
  loadingLabel = "…",
  errorLabel = "!",
  retryLabel = "Retry",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [q, options]);

  const toggle = (val) => {
    onChange(values.includes(val) ? values.filter((v) => v !== val) : [...values, val]);
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testid ? `${testid}-trigger` : undefined}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-left text-sm text-zinc-100 transition-colors hover:border-zinc-500 focus:outline-none focus-visible:border-emerald-500"
      >
        <span className={`truncate ${values.length === 0 ? "text-zinc-500" : ""}`}>
          {values.length > 0 ? selectedLabel(values.length) : placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {values.length > 0 && (
            <span className="rounded-full bg-emerald-500/20 px-1.5 text-[10px] font-bold text-emerald-300">
              {values.length}
            </span>
          )}
          <ChevronDown size={14} className="text-zinc-500" aria-hidden />
        </span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 w-72 max-w-[86vw] overflow-hidden rounded-xl border border-zinc-700 bg-zinc-900 shadow-xl shadow-black/40">
          <div className="flex items-center gap-2 border-b border-zinc-800 px-3 py-2">
            <Search size={13} className="shrink-0 text-zinc-500" aria-hidden />
            <input
              autoFocus
              data-testid={testid ? `${testid}-search` : undefined}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none"
            />
            {values.length > 0 && (
              <button
                type="button"
                data-testid={testid ? `${testid}-reset` : undefined}
                onClick={() => { onChange([]); setQ(""); }}
                className="flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
              >
                <X size={11} aria-hidden /> {resetLabel}
              </button>
            )}
          </div>
          <div role="listbox" aria-multiselectable className="max-h-60 overflow-y-auto py-1">
            {filtered.map((o) => {
              const active = values.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  onClick={() => toggle(o.value)}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      active
                        ? "border-emerald-500 bg-emerald-500 text-zinc-950"
                        : "border-zinc-600 bg-zinc-900"
                    }`}
                  >
                    {active && <Check size={11} strokeWidth={3} aria-hidden />}
                  </span>
                  <span className="flex-1 truncate">{o.label}</span>
                  {typeof o.count === "number" && (
                    <span className="text-[10px] text-zinc-500">{o.count}</span>
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-center text-xs text-zinc-500" data-testid={testid ? `${testid}-status` : undefined}>
                {loading ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin" aria-hidden /> {loadingLabel}
                  </span>
                ) : error ? (
                  <span className="inline-flex flex-col items-center gap-1.5 text-amber-300">
                    {errorLabel}
                    {onRetry && (
                      <button
                        type="button"
                        data-testid={testid ? `${testid}-retry` : undefined}
                        onClick={onRetry}
                        className="rounded-md border border-zinc-700 px-2 py-0.5 text-[11px] text-zinc-200 hover:border-zinc-500"
                      >
                        {retryLabel}
                      </button>
                    )}
                  </span>
                ) : "—"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
